// ============================================================================
// ORDERS · Order Desk — every read, and the ONE write door (B2a)
// ============================================================================
// The Desk's data path. It touches orders_pis, orders_colorways,
// orders_colorway_lenses, suppliers, orders_collections and two RPCs — and
// NOTHING ELSE. In particular it never names orders_lines: the Desk and the
// Book are independent stores (David, 07.09), which is what makes the daily
// import structurally unable to reach a Desk order.
//
// Query keys all sit under ['orders','desk', …] so one invalidate after a
// create refreshes the list without touching the Book's ['orders','lines'].
// ============================================================================

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../supabase';

export const KEY = ['orders', 'desk'];

/** PostgREST's two ways of saying "that function is not in this database yet":
 *  42883 is Postgres' own undefined_function, PGRST202 is the schema cache
 *  reporting the same absence before a reload lands. They are worth telling
 *  apart from every other failure, because a screen that renders an empty list
 *  here would be lying: the bank is not empty, it is not installed. */
export const isMissingFn = (e) => e?.code === '42883' || e?.code === 'PGRST202';

/** Retry anything transient, never a function that does not exist — retrying
 *  an absent RPC three times just makes the honest message arrive later. */
export const retryUnlessMissing = (n, e) => !isMissingFn(e) && n < 2;

/* ---------------------------------------------------------------- the list */

/** Every order, newest first, with its colourways folded in for the totals.
 *  One request via PostgREST's embed rather than N+1 (Conv #167's spirit:
 *  let the FK do the join). */
export function useDeskOrders({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'orders'],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders_pis')
        .select('id,pi_no,item_no,model_name,supplier_code,factory_code,collection_code,'
              + 'type_set,status,source,sample_requirement,sample_note,order_date,sent_at,discarded_at,'
              + 'dept,designer,gender,material,shape,sub_shape,notes,dev_refs,created_at,'
              + 'orders_colorways(id,item_code,color_name,qty,unit_price,retail_price,'
              + 'frame_color,temple_color,remark,is_option,lens_no,barcode,sort_order)')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []).map((r) => ({
        ...r,
        colorways: [...(r.orders_colorways || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
      }));
    },
  });
  return { orders: q.data || [], isLoading: q.isLoading, isPending: q.isPending, isFetching: q.isFetching,
    dataUpdatedAt: q.dataUpdatedAt, isError: q.isError, error: q.error, refetch: q.refetch };
}

/** One order, with its colourways AND their lens references. The lenses are a
 *  second request because they hang off the colourway, not the order. */
export function useDeskOrder(id, { enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'order', id || 'none'],
    enabled: enabled && !!id,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders_pis')
        .select('*,orders_colorways(*)')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const cws = [...(data.orders_colorways || [])]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      if (cws.length === 0) return { ...data, colorways: [] };

      const { data: lensRows, error: lensErr } = await supabase
        .from('orders_colorway_lenses')
        .select('id,colorway_id,lens_id,lens_code,sort_order,note')
        .in('colorway_id', cws.map((c) => c.id))
        .order('sort_order');
      if (lensErr) throw lensErr;

      const byCw = new Map();
      (lensRows || []).forEach((l) => {
        if (!byCw.has(l.colorway_id)) byCw.set(l.colorway_id, []);
        byCw.get(l.colorway_id).push(l);
      });
      return { ...data, colorways: cws.map((c) => ({ ...c, lenses: byCw.get(c.id) || [] })) };
    },
  });
  return { order: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error };
}

/** What the Book already knows about this model.
 *
 *  GATE G1. The facts live as plain columns on the two Book tables —
 *  `material`, `shape`, `sub_shape`, `supplier`, `collection`, `type`. NOTE
 *  what is NOT there: `orders_book_lines` and `orders_lines` carry no `gender`
 *  and no `department` (verified against the live schema, Conv #90). Those two
 *  live in `sales_data_master`, so they arrive with the colour RPC, not here.
 *
 *  THE "LATEST" RULE: the LIVE table wins over the history, because a
 *  (book, collection) with live rows is owned by the daily import and its
 *  history rows are dropped at read time anyway (model/bookScope.mergeBookRows);
 *  within each table, the newest `order_date`. Ordering by `collection` was
 *  rejected — it is free text, and 'AW22-23' sorts after 'SS2027' lexically
 *  while being four years older.
 *
 *  Both reads go out together inside ONE queryFn, so a pick costs one settled
 *  query, not two round trips.
 */
export function useModelBookFacts(model, { enabled = true } = {}) {
  const m = (model || '').trim().toUpperCase();
  const cols = 'item_no,material,shape,sub_shape,supplier,collection,type,order_date';
  const q = useQuery({
    queryKey: [...KEY, 'bookFacts', m],
    enabled: enabled && m.length > 1,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessMissing,
    queryFn: async () => {
      const one = (tbl) => supabase.from(tbl).select(cols)
        .eq('item_no', m).order('order_date', { ascending: false }).limit(1);
      const [live, hist] = await Promise.all([one('orders_lines'), one('orders_book_lines')]);
      if (live.error) throw live.error;
      if (hist.error) throw hist.error;
      const row = (live.data && live.data[0]) || (hist.data && hist.data[0]) || null;
      return row ? { ...row, from: live.data && live.data[0] ? 'live' : 'history' } : null;
    },
  });
  return { facts: q.data || null, isLoading: q.isLoading };
}

/** Is this PI number already on an order?
 *
 *  ONE cheap read, and only for a number that already passes the format check —
 *  there is no point asking the database about `2609`. `orders_pis` SELECT is
 *  `to authenticated using (has_orders_access())` (Gate G4,
 *  sql/2026-08-31-orders-p0-1-rls-align.sql:38, applied 2026-08-31), so an
 *  Orders user can run this directly; no definer RPC is needed for it.
 *
 *  The caller passes a DEBOUNCED value, so this is one request per settled
 *  number rather than one per keystroke. The server's own `pi_no_taken` stays
 *  as the last line of defence — this only saves the operator from finding out
 *  after they press Create.
 */
export function usePiNoTaken(piNo, { enabled = true } = {}) {
  const v = (piNo || '').trim().toUpperCase();
  const q = useQuery({
    queryKey: [...KEY, 'piTaken', v],
    enabled: enabled && v.length > 0,
    staleTime: 30 * 1000,
    retry: retryUnlessMissing,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders_pis')
        .select('id,item_no,status')
        .eq('pi_no', v)
        .limit(1);
      if (error) throw error;
      return (data && data[0]) || null;
    },
  });
  return { taken: q.data || null, isChecking: q.isFetching };
}

/* ------------------------------------------------------ the pick-from lists */

/** The 40 supplier codes. Small and stable — cached for the session. */
export function useDeskSuppliers({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'suppliers'],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers').select('code,name,active').order('code');
      if (error) throw error;
      return data || [];
    },
  });
  return { suppliers: q.data || [], isLoading: q.isLoading };
}

/** The seeded collection codes. */
export function useDeskCollections({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'collections'],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders_collections')
        .select('code,season,season_year,variant,active,sort_order')
        .order('sort_order', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  return { collections: q.data || [], isLoading: q.isLoading };
}

/** The lens bank, through the Desk's own adapter RPC. lens_bank's read policy
 *  is gated on the LENS portal, so a direct client read would return nothing
 *  for an Orders editor without lens access — orders_lens_search is the door
 *  (SECURITY DEFINER, has_orders_access() inside). Server-side search, capped:
 *  the bank holds ~598 rows and none of them belong in a dropdown at once. */
export function useLensSearch(term, { enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'lens', term || ''],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('orders_lens_search', { p_q: term || null, p_limit: 40 });
      if (error) throw error;
      return data || [];
    },
  });
  return { lenses: q.data || [], isLoading: q.isLoading, isError: q.isError };
}

/** How often each lens code has actually been ordered on the Desk, rolled up
 *  from orders_colorway_lenses. GATE E: the table exists, so the count is real
 *  — and it is legitimately EMPTY until orders exist, which is why a chip
 *  renders its count only when there is one. The prototype's percentages are
 *  invented and do not ship. */
export function useLensUsage({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: [...KEY, 'lensUsage'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders_colorway_lenses').select('lens_code').limit(2000);
      if (error) throw error;
      const out = {};
      (data || []).forEach((r) => { if (r.lens_code) out[r.lens_code] = (out[r.lens_code] || 0) + 1; });
      return out;
    },
  });
  return { uses: q.data || {}, isLoading: q.isLoading };
}

/* ------------------------------------------------------- the ONE write door */

/**
 * Creates one order. Everything lands in a single transaction inside
 * orders_pi_create, so a half-written order cannot exist — and the RPC is
 * SECURITY INVOKER, so RLS (has_orders_editor) is the authorization rather
 * than anything this file decides.
 *
 * Returns { ok, id, error } and never throws, so a caller's click handler
 * cannot reach the error boundary (Conv #107's discipline).
 */
export function useCreateOrder() {
  const qc = useQueryClient();
  return useCallback(async (payload) => {
    const { data, error } = await supabase.rpc('orders_pi_create', payload);
    if (error) return { ok: false, error: error.message || String(error) };
    if (data && data.error) return { ok: false, error: data.error, detail: data };
    await qc.invalidateQueries({ queryKey: [...KEY, 'orders'] });
    return { ok: true, id: data?.id, colorways: data?.colorways, lenses: data?.lenses };
  }, [qc]);
}
